import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Option, Input, Question } from "@/components/lp/ProjectBuilderForm";
import { Reveal } from "@/components/yanre/Reveal";
import { COUNTRIES } from "@/components/lp/data";
import { PhoneIntl, isValidPhoneNumber } from "@/components/lp/PhoneIntl";
import { track, attribution, metaClickIds, fireProjectReceived } from "@/lib/track";
import { FormIntro } from "../FormIntro";
import { readConsent } from "@/lib/consent";
import { INSTITUTIONAL_WEBSITE_URL } from "@/config";
import { useLocale } from "@/i18n/LocaleContext";

const API = process.env.REACT_APP_BACKEND_URL;

// Personal Trainer Studio qualification form — 4-STEP conversion-optimized
// version (approved 2026-07). Same architecture as the Project Builder:
// canonical English VALUES, canonical dataLayer events, project_received with
// shared event_id for browser/server deduplication. Removed-from-form
// qualification fields keep their HubSpot properties for later qualification.
// Options are [canonical value, translation key, English default].
const PACKAGES = [
  ["starter", "pts.opt.pkg.starter", "STARTER"],
  ["studio-pro", "pts.opt.pkg.studio-pro", "STUDIO PRO"],
  ["need-help", "pts.opt.pkg.need-help", "I need help choosing"],
];
const TIMELINES = [
  ["1-2", "pts.opt.time.1-2", "1–2 months"],
  ["2-4", "pts.opt.time.2-4", "2–4 months"],
  ["4-6", "pts.opt.time.4-6", "4–6 months"],
  ["6-plus", "pts.opt.time.6-plus", "More than 6 months"],
];
const LOCATION = [
  ["confirmed", "pts.opt.loc.confirmed", "Yes"],
  ["looking", "pts.opt.loc.looking", "Currently looking"],
  ["not-yet", "pts.opt.loc.not-yet", "Not yet"],
];
const SIZES = [
  ["under-50", "pts.opt.size.under-50", "Under 50 m²"],
  ["50-100", "pts.opt.size.50-100", "50–100 m²"],
  ["100-200", "pts.opt.size.100-200", "100–200 m²"],
  ["over-200", "pts.opt.size.over-200", "Over 200 m²"],
  ["undefined", "pts.opt.size.undefined", "Not defined yet"],
];
// Investment BUDGET — always displayed in USD in every locale/market.
// These are budget ranges, NOT package prices.
const INVESTMENTS = [
  ["under-20k", "pts.opt.inv.under-20k", "Under USD 20,000"],
  ["20-30k", "pts.opt.inv.20-30k", "USD 20,000–30,000"],
  ["30-50k", "pts.opt.inv.30-50k", "USD 30,000–50,000"],
  ["50-80k", "pts.opt.inv.50-80k", "USD 50,000–80,000"],
  ["80k-plus", "pts.opt.inv.80k-plus", "Over USD 80,000"],
];
const PROFILES = [
  ["personal-trainer", "pts.opt.role.personal-trainer", "Personal Trainer"],
  ["gym-owner-operator", "pts.opt.role.gym-owner-operator", "Gym Owner / Operator"],
  ["investor-entrepreneur", "pts.opt.role.investor-entrepreneur", "Investor / Entrepreneur"],
];

// Profile values map onto existing yanre_decision_role CRM options
// (trainer / owner / investor all pre-exist — no new CRM values).
const ROLE_MAP = {
  "personal-trainer": "trainer",
  "gym-owner-operator": "owner",
  "investor-entrepreneur": "investor",
};

const EMPTY = {
  packageInterest: "", openingTimeline: "",
  locationStatus: "", studioSize: "",
  investmentBand: "", profile: "",
  country: "", city: "", fullName: "", email: "", phone: "", phoneCountry: "PT", phoneTouched: false, company: "",
  privacyConsent: false, marketingConsent: false,
};

const TOTAL = 4;
const STEP_NAMES = ["Studio", "Space", "Investment", "Contact"];

let savedForm = null; // progress survives SPA navigation, same pattern as the Project Builder

// Phone country suggestion per locale (fallback: Portugal). Always editable.
const LOCALE_PHONE_DEFAULT = { fr: "FR", de: "DE" };

export const PTStudioForm = ({ packagePreselect }) => {
  const { t, locale } = useLocale();
  // Locale-based phone country suggestion (France +33, Germany +49, …).
  const initialPhoneCountry = LOCALE_PHONE_DEFAULT[locale] || "PT";
  const [step, setStep] = useState(savedForm?.step || 1);
  const [a, setA] = useState(() => savedForm?.answers || { ...EMPTY, phoneCountry: initialPhoneCountry });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [conversionId, setConversionId] = useState(null);
  const startedRef = useRef(Boolean(savedForm));

  useEffect(() => {
    track("form_step_view", { form: "personal-trainer-studio", step, totalSteps: TOTAL });
  }, [step]);

  useEffect(() => {
    savedForm = { step, answers: a };
  }, [step, a]);

  // Package CTAs on the page preselect the form answer (visitor can still change it).
  useEffect(() => {
    if (packagePreselect) set("packageInterest", packagePreselect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packagePreselect]);

  const set = (k, v) => {
    if (!startedRef.current) {
      startedRef.current = true;
      track("form_start", { form: "personal-trainer-studio" });
    }
    setA((p) => ({ ...p, [k]: v }));
  };

  const valid = useMemo(() => {
    switch (step) {
      case 1: return !!a.packageInterest && !!a.openingTimeline;
      case 2: return !!a.locationStatus && !!a.studioSize;
      case 3: return !!a.investmentBand && !!a.profile;
      case 4: return !!(a.country && a.city.trim() && a.fullName.trim() && /\S+@\S+\.\S+/.test(a.email) && a.phone.trim() && isValidPhoneNumber(a.phone) && a.privacyConsent);
      default: return false;
    }
  }, [step, a]);

  const phoneError = () =>
    step === 4 && a.phone.trim() && !isValidPhoneNumber(a.phone);

  const next = () => {
    if (!valid) {
      toast.error(phoneError()
        ? t("err.phoneInvalid", "Please enter a valid phone number for the selected country.")
        : t("pts.form.err.step", "Please complete the required fields to continue."));
      return;
    }
    track("form_step_complete", { form: "personal-trainer-studio", step });
    setStep((s) => Math.min(s + 1, TOTAL));
  };

  const submit = async () => {
    if (!valid) {
      toast.error(phoneError()
        ? t("err.phoneInvalid", "Please enter a valid phone number for the selected country.")
        : t("pts.form.err.step", "Please complete the required fields to continue."));
      return;
    }
    setBusy(true);
    // Full Name → firstName / lastName (single field per spec; backend model needs both)
    const parts = a.fullName.trim().split(/\s+/);
    const firstName = parts[0];
    const lastName = parts.slice(1).join(" ");
    const payload = {
      brand: "YANRE FITNESS EUROPE",
      opportunityType: "Personal Trainer Studio",
      landingPage: "personal-trainer-studio",
      projectType: "pt-studio",
      decisionRole: ROLE_MAP[a.profile] || a.profile,
      packageInterest: a.packageInterest,
      locationStatus: a.locationStatus,
      facilityArea: a.studioSize,
      openingTimeline: a.openingTimeline,
      investmentBand: a.investmentBand,
      // Removed-from-form qualification fields: not collected here anymore.
      // Empty values are dropped before HubSpot; CRM properties stay intact.
      projectNeed: [],
      country: a.country, city: a.city.trim(),
      firstName, lastName, company: a.company.trim(),
      email: a.email.trim(), phone: a.phone,
      preferredContact: "whatsapp",
      privacyConsent: a.privacyConsent, marketingConsent: a.marketingConsent,
      language: locale,
      leadSourceDetail: "landing:personal-trainer-studio",
      ...attribution(),
      pageUrl: window.location.href,
      consentAdvertising: !!readConsent()?.advertising,
      ...metaClickIds(),
    };
    try {
      const res = await fetch(`${API}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "submit failed");
      track("form_submit", { form: "personal-trainer-studio", leadId: data.leadId });
      savedForm = null;
      // Conversion gate (2026-09 audit): project_received may fire ONLY for a
      // genuine backend-persisted submission (persisted:true + immutable
      // submissionId). A mocked/bare HTTP 200 shows the success UI but can
      // NEVER fire the Google Ads conversion.
      const confirmed = data && data.persisted === true && typeof data.submissionId === "string" && data.submissionId.length > 0;
      setConversionId(confirmed ? data.submissionId : null);
      setDone(data.leadId || data.submissionId || "received");
    } catch {
      toast.error(t("pts.form.err.submit", "Something went wrong while sending your project. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (conversionId) fireProjectReceived(conversionId, { form: "personal-trainer-studio" });
  }, [conversionId]);

  return (
    <section id="studio-project" className="section-darker relative scroll-mt-20 overflow-hidden border-t border-carbon-700" data-testid="pt-form-section">
      <div className="grid-lines absolute inset-0 opacity-40" />
      <div className="container-edge relative py-16 lg:py-24">
        <div className="mx-auto max-w-3xl">
        {done ? (
          <Reveal>
            <div className="border border-carbon-700 bg-carbon-900 p-10 text-center sm:p-14" data-testid="pt-form-success">
              <div className="mx-auto mb-8 flex h-16 w-16 items-center justify-center border border-signal">
                <Check className="h-7 w-7 text-signal" />
              </div>
              <h2 className="font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl">
                {t("pts.success.title", "Your studio project has been received.")}
              </h2>
              <p className="mx-auto mt-6 max-w-md text-base font-light leading-relaxed text-titanium/75">
                {t("pts.success.body", "A YANRE specialist will review your information and contact you to discuss your studio, equipment requirements and next steps.")}
              </p>
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-titanium/40" data-testid="pt-form-reference">
                {t("pts.success.reference", "Reference")}: {done.slice(0, 8).toUpperCase()}
              </p>
              <a
                href={INSTITUTIONAL_WEBSITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("visit_website_click", { placement: "form_success", page: "personal-trainer-studio" })}
                className="btn-signal group mt-10"
                data-testid="pt-success-visit-website"
              >
                {t("pts.success.cta", "Visit YANRE FITNESS")}
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        ) : (
          <>
            <Reveal>
              <div className="mb-6 flex items-center gap-4">
                <span className="h-px w-10 bg-signal" />
                <span className="overline">{t("pts.form.label", "Start Your Studio Project")}</span>
              </div>
              <h2 className="font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-6xl" data-testid="pt-form-title">
                {t("pts.form.h2a", "Tell us about")}{" "}
                <span className="text-signal">{t("pts.form.h2b", "your studio.")}</span>
              </h2>
              <FormIntro
                testidPrefix="pt-form"
                sub1={t("pts.form.intro.sub1", "4 QUICK STEPS.")}
                sub2={t("pts.form.intro.sub2", "ONE COMPLETE STUDIO SOLUTION.")}
                support={t("pts.form.intro.support", "Tell us about your studio in just 4 simple steps. Our team will understand your needs and help you build the right solution — with direct factory supply from YANRE FITNESS China, professional consultation and after-sales support.")}
                steps={[1, 2, 3, 4].map((n) => t(`pts.step.${n}`, STEP_NAMES[n - 1]))}
                minute={t("pts.form.intro.minute", "1 MINUTE TO COMPLETE")}
              />
            </Reveal>

            <div className="mt-10 border border-carbon-600 bg-carbon-900 p-6 sm:p-10" data-testid="pt-form">
              <div className="mb-8">
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/50">
                  <span data-testid="pt-form-step-label">{t("pts.form.progress", "Step")} {step} / {TOTAL} — {t(`pts.step.${step}`, STEP_NAMES[step - 1])}</span>
                </div>
                <div className="mt-3 h-px w-full bg-carbon-700">
                  <div className="h-px bg-signal transition-all duration-500" style={{ width: `${(step / TOTAL) * 100}%` }} data-testid="pt-form-progress-bar" />
                </div>
              </div>

              {step === 1 && (
                <>
                  <Question label={t("pts.q.package", "Which studio package are you interested in?")} required>
                    {PACKAGES.map(([v, k, d]) => (
                      <Option key={v} testId={`f-pts-package-${v}`} selected={a.packageInterest === v} onClick={() => set("packageInterest", v)}>{t(k, d)}</Option>
                    ))}
                  </Question>
                  <Question label={t("pts.q.timeline", "When do you plan to open your studio?")} required>
                    {TIMELINES.map(([v, k, d]) => (
                      <Option key={v} testId={`f-pts-timeline-${v}`} selected={a.openingTimeline === v} onClick={() => set("openingTimeline", v)}>{t(k, d)}</Option>
                    ))}
                  </Question>
                </>
              )}

              {step === 2 && (
                <>
                  <Question label={t("pts.q.location", "Do you already have a location?")} required>
                    {LOCATION.map(([v, k, d]) => (
                      <Option key={v} testId={`f-pts-location-${v}`} selected={a.locationStatus === v} onClick={() => set("locationStatus", v)}>{t(k, d)}</Option>
                    ))}
                  </Question>
                  <Question label={t("pts.q.size", "Approximate studio size:")} required>
                    {SIZES.map(([v, k, d]) => (
                      <Option key={v} testId={`f-pts-size-${v}`} selected={a.studioSize === v} onClick={() => set("studioSize", v)}>{t(k, d)}</Option>
                    ))}
                  </Question>
                </>
              )}

              {step === 3 && (
                <>
                  <Question label={t("pts.q.investment", "What is your estimated investment budget? (USD)")} required>
                    {INVESTMENTS.map(([v, k, d]) => (
                      <Option key={v} testId={`f-pts-investment-${v}`} selected={a.investmentBand === v} onClick={() => set("investmentBand", v)}>{t(k, d)}</Option>
                    ))}
                  </Question>
                  <Question label={t("pts.q.areYou", "Which best describes you?")} required>
                    {PROFILES.map(([v, k, d]) => (
                      <Option key={v} testId={`f-pts-role-${v}`} selected={a.profile === v} onClick={() => set("profile", v)}>{t(k, d)}</Option>
                    ))}
                  </Question>
                </>
              )}

              {step === 4 && (
                <>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-titanium/50">
                        {t("pts.q.country", "Country")} <span className="text-signal">*</span>
                      </span>
                      <select
                        value={a.country}
                        onChange={(e) => {
                          const c = COUNTRIES.find((x) => x.name === e.target.value);
                          // Project country only SUGGESTS the phone country
                          // (until the visitor picks a phone country or types);
                          // the visitor can always change it independently.
                          setA((p) => ({
                            ...p,
                            country: e.target.value,
                            phoneCountry: !p.phoneTouched && !p.phone && c?.iso2 ? c.iso2 : p.phoneCountry,
                          }));
                        }}
                        data-testid="f-pts-country"
                        className="w-full rounded-none border border-carbon-600 bg-carbon-800 px-4 py-3.5 text-sm text-white outline-none transition-colors focus:border-signal"
                      >
                        <option value="">{t("pts.q.selectCountry", "Select country")}</option>
                        {COUNTRIES.map(({ name }) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                    </label>
                    <Input label={t("pts.q.city", "City")} required value={a.city} onChange={(e) => set("city", e.target.value)} data-testid="f-pts-city" />
                    <Input label={t("pts.q.fullName", "Full Name")} required value={a.fullName} onChange={(e) => set("fullName", e.target.value)} data-testid="f-pts-fullname" autoComplete="name" />
                    <Input label={t("pts.q.email", "Email")} required type="email" value={a.email} onChange={(e) => set("email", e.target.value)} data-testid="f-pts-email" autoComplete="email" />
                    <div>
                      <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-titanium/50">
                        {t("pts.q.phone", "Phone / WhatsApp")} <span className="text-signal">*</span>
                      </span>
                      <PhoneIntl
                        value={a.phone}
                        country={a.phoneCountry || undefined}
                        onChange={(v) => set("phone", v)}
                        onCountryChange={(c) => set("phoneCountry", c)}
                        onUserCountrySelect={() => set("phoneTouched", true)}
                        invalid={Boolean(a.phone) && !isValidPhoneNumber(a.phone)}
                        inputTestId="f-pts-phone"
                        selectTestId="f-pts-phone-country"
                      />
                    </div>
                    <Input label={t("pts.q.company", "Company / Studio Name (optional)")} value={a.company} onChange={(e) => set("company", e.target.value)} data-testid="f-pts-company" />
                  </div>
                  <label className="mt-6 flex cursor-pointer items-start gap-3 text-sm font-light text-titanium/70">
                    <input type="checkbox" checked={a.privacyConsent} onChange={(e) => set("privacyConsent", e.target.checked)} className="mt-1 accent-[#d10000]" data-testid="f-pts-privacy" />
                    <span>{t("consent.privacy", "I agree to YANRE FITNESS EUROPE processing my information to contact me about this project.")} <span className="text-signal">*</span></span>
                  </label>
                  <label className="mt-3 flex cursor-pointer items-start gap-3 text-sm font-light text-titanium/50">
                    <input type="checkbox" checked={a.marketingConsent} onChange={(e) => set("marketingConsent", e.target.checked)} className="mt-1 accent-[#d10000]" data-testid="f-pts-marketing" />
                    <span>{t("consent.marketing", "I also want to receive occasional YANRE FITNESS EUROPE news and offers. (Optional)")}</span>
                  </label>
                </>
              )}

              <div className="mt-10 flex items-center justify-between">
                {step > 1 ? (
                  <button type="button" onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-titanium/60 transition-colors hover:text-white" data-testid="pt-form-back-btn">
                    <ArrowLeft className="h-3.5 w-3.5 rtl:-scale-x-100" />
                    {t("pts.form.back", "Back")}
                  </button>
                ) : <span />}
                {step < TOTAL ? (
                  <button type="button" onClick={next} className="btn-signal group" data-testid="pt-form-next-btn">
                    {t("pts.form.continue", "Continue")}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                ) : (
                  <button type="button" onClick={submit} disabled={busy} className="btn-signal group disabled:opacity-60" data-testid="pt-form-submit-btn">
                    {busy ? t("pts.form.sending", "Sending…") : t("pts.form.submit", "Get My Studio Project")}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                )}
              </div>
            </div>
          </>
        )}
        </div>
      </div>
    </section>
  );
};
